
import React from "react";
import PropTypes from "prop-types";

import StudioShell from "../StudioShell";
import StudioContributionSlot from "./StudioContributionSlot";
import useStudioContributions from "./useStudioContributions";

const EMPTY_SLOTS = Object.freeze({});

function composeAdditiveSlot({
  type,
  baseSlot,
  contributions,
  context,
}) {
  if (contributions.length === 0) {
    return baseSlot;
  }

  return (
    <>
      {baseSlot}
      <StudioContributionSlot
        type={type}
        contributions={contributions}
        context={context}
      />
    </>
  );
}

function resolveCanvasSlot({
  baseSlot,
  contributions,
  context,
}) {
  if (baseSlot !== null && baseSlot !== undefined) {
    return baseSlot;
  }

  if (contributions.length === 0) {
    return null;
  }

  if (contributions.length > 1) {
    throw new Error(
      "StudioRuntimeComposition cannot resolve more than one canvas contribution."
    );
  }

  return (
    <StudioContributionSlot
      type="canvas"
      contributions={contributions}
      context={context}
    />
  );
}

/**
 * React composition boundary between StudioRuntime and presentation.
 *
 * StudioRuntime registers and resolves capabilities.
 * This component materializes their React contributions and injects
 * the resulting slots into StudioShell.
 */
export default function StudioRuntimeComposition({
  mode = "standalone",
  slots = EMPTY_SLOTS,
}) {
  const toolbarContributions = useStudioContributions("toolbar");
  const sidebarContributions = useStudioContributions("sidebar");
  const inspectorContributions = useStudioContributions("inspector");
  const canvasContributions = useStudioContributions("canvas");

  const contributionContext = {
    mode,
    slots,
  };

  const header = composeAdditiveSlot({
    type: "toolbar",
    baseSlot: slots.header ?? null,
    contributions: toolbarContributions,
    context: contributionContext,
  });

  const explorer = composeAdditiveSlot({
    type: "sidebar",
    baseSlot: slots.explorer ?? null,
    contributions: sidebarContributions,
    context: contributionContext,
  });

  const inspector = composeAdditiveSlot({
    type: "inspector",
    baseSlot: slots.inspector ?? null,
    contributions: inspectorContributions,
    context: contributionContext,
  });

  const canvas = resolveCanvasSlot({
    baseSlot: slots.canvas ?? null,
    contributions: canvasContributions,
    context: contributionContext,
  });

  return (
    <StudioShell
      mode={mode}
      header={header}
      explorer={explorer}
      canvas={canvas}
      inspector={inspector}
      assets={slots.assets ?? null}
      statusBar={slots.statusBar ?? null}
    />
  );
}

StudioRuntimeComposition.propTypes = {
  mode: PropTypes.oneOf([
    "standalone",
    "embedded",
    "fullscreen",
  ]),
  slots: PropTypes.shape({
    header: PropTypes.node,
    explorer: PropTypes.node,
    canvas: PropTypes.node,
    inspector: PropTypes.node,
    assets: PropTypes.node,
    statusBar: PropTypes.node,
  }),
};

export {
  composeAdditiveSlot,
  resolveCanvasSlot,
};
